import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from '../../../utils/classNames';
import './index.scss';

const ChevronDown = () => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const Check = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

export default function GlassSelect({
    id,
    name,
    label,
    icon,
    value,
    options,
    onChange,
    disabled = false,
    placeholder,
    ariaLabel,
    className = '',
}) {
    const fallbackId = useId();
    const baseId = id || fallbackId;
    const labelId = `${baseId}-label`;
    const listId = `${baseId}-list`;

    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const listRef = useRef(null);
    const optionRefs = useRef([]);

    const selectedIndex = useMemo(
        () => options.findIndex((option) => option.value === value),
        [options, value],
    );
    const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
    const displayValue = selected?.label || placeholder || '';

    const close = useCallback(() => {
        setIsOpen(false);
        setActiveIndex(-1);
    }, []);

    const open = useCallback(() => {
        if (disabled) return;
        setActiveIndex(options.length ? (selectedIndex >= 0 ? selectedIndex : 0) : -1);
        setIsOpen(true);
    }, [disabled, options.length, selectedIndex]);

    const commit = useCallback((nextValue) => {
        if (typeof onChange === 'function') {
            // Mirror native <select> onChange shape so consumers expecting
            // event.target.value continue to work.
            onChange({ target: { value: nextValue, name } });
        }
        close();
        triggerRef.current?.focus();
    }, [onChange, name, close]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handlePointerDown = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                close();
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [isOpen, close]);

    useEffect(() => {
        if (!isOpen || activeIndex < 0) return;
        const node = optionRefs.current[activeIndex];
        if (node && typeof node.scrollIntoView === 'function') {
            node.scrollIntoView({ block: 'nearest' });
        }
    }, [isOpen, activeIndex]);

    const moveActiveIndex = useCallback((direction) => {
        setActiveIndex((current) => {
            if (!options.length) return -1;
            const start = current >= 0 ? current : selectedIndex;
            if (direction === 'next') {
                return (start + 1 + options.length) % options.length;
            }
            return start <= 0 ? options.length - 1 : start - 1;
        });
    }, [options.length, selectedIndex]);

    const commitActiveOption = useCallback(() => {
        const index = activeIndex >= 0 ? activeIndex : selectedIndex;
        const target = options[index];
        if (target) commit(target.value);
    }, [activeIndex, commit, options, selectedIndex]);

    const handleTriggerKeyDown = (event) => {
        if (disabled) return;
        if (isOpen) {
            if (event.key === 'Escape') {
                event.preventDefault();
                close();
                return;
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                moveActiveIndex('next');
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                moveActiveIndex('previous');
                return;
            }
            if (event.key === 'Home') {
                event.preventDefault();
                setActiveIndex(options.length ? 0 : -1);
                return;
            }
            if (event.key === 'End') {
                event.preventDefault();
                setActiveIndex(options.length ? options.length - 1 : -1);
                return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                commitActiveOption();
                return;
            }
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
        }
    };

    const handleListKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            triggerRef.current?.focus();
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveActiveIndex('next');
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            moveActiveIndex('previous');
            return;
        }
        if (event.key === 'Home') {
            event.preventDefault();
            setActiveIndex(options.length ? 0 : -1);
            return;
        }
        if (event.key === 'End') {
            event.preventDefault();
            setActiveIndex(options.length ? options.length - 1 : -1);
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            commitActiveOption();
        }
    };

    const triggerLabelledBy = label ? labelId : undefined;

    return (
        <div
            ref={containerRef}
            className={classNames(
                'glass-select',
                disabled && 'glass-select--disabled',
                isOpen && 'glass-select--open',
                className,
            )}
        >
            {label ? (
                <span id={labelId} className="glass-select__label-hidden">
                    {label}
                </span>
            ) : null}
            <button
                ref={triggerRef}
                id={baseId}
                name={name}
                type="button"
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listId}
                aria-labelledby={triggerLabelledBy}
                aria-label={ariaLabel}
                aria-disabled={disabled || undefined}
                className="glass-select__trigger"
                disabled={disabled}
                onClick={() => (isOpen ? close() : open())}
                onKeyDown={handleTriggerKeyDown}
            >
                {icon ? (
                    <span className="glass-select__icon" aria-hidden="true">
                        {icon}
                    </span>
                ) : null}
                <span className="glass-select__copy">
                    {label ? (
                        <span className="glass-select__label" aria-hidden="true">
                            {label}
                        </span>
                    ) : null}
                    <span className="glass-select__value">{displayValue}</span>
                </span>
                <span className="glass-select__chev" aria-hidden="true">
                    <ChevronDown />
                </span>
            </button>

            {isOpen ? (
                <ul
                    ref={listRef}
                    id={listId}
                    role="listbox"
                    tabIndex={-1}
                    aria-labelledby={triggerLabelledBy}
                    className="glass-select__menu"
                    onKeyDown={handleListKeyDown}
                >
                    {options.map((option, index) => {
                        const isSelected = option.value === value;
                        const isActive = index === activeIndex;
                        return (
                            <li
                                key={option.value}
                                ref={(node) => { optionRefs.current[index] = node; }}
                                role="option"
                                aria-selected={isSelected}
                                className={classNames(
                                    'glass-select__option',
                                    isActive && 'glass-select__option--active',
                                    isSelected && 'glass-select__option--selected',
                                )}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => commit(option.value)}
                            >
                                <span className="glass-select__option-check" aria-hidden="true">
                                    {isSelected ? <Check /> : null}
                                </span>
                                <span className="glass-select__option-label">{option.label}</span>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
}

GlassSelect.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.string,
    icon: PropTypes.node,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired,
    })).isRequired,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
};
